import React, { useState, useEffect } from "react";
import { X, Check, Sliders, RotateCcw } from "lucide-react";
import { saveFibTemplate, loadSavedFibTemplate } from "../services/drawingEngine";

export const DEFAULT_FIB_LEVELS = [
  { id: "0", enabled: true, ratio: 0.0, color: "#787b86" },
  { id: "0.236", enabled: true, ratio: 0.236, color: "#f23645" },
  { id: "0.382", enabled: true, ratio: 0.382, color: "#ff9800" },
  { id: "0.5", enabled: true, ratio: 0.5, color: "#4caf50" },
  { id: "0.618", enabled: true, ratio: 0.618, color: "#089981" },
  { id: "0.786", enabled: true, ratio: 0.786, color: "#00bcd4" },
  { id: "1", enabled: true, ratio: 1.0, color: "#787b86" },
  { id: "1.618", enabled: true, ratio: 1.618, color: "#2962ff" },
  { id: "2.618", enabled: true, ratio: 2.618, color: "#f23645" },
  { id: "3.618", enabled: true, ratio: 3.618, color: "#9c27b0" },
  { id: "4.236", enabled: true, ratio: 4.236, color: "#e91e63" },
  { id: "1.272", enabled: false, ratio: 1.272, color: "#f59e0b" },
  { id: "1.414", enabled: false, ratio: 1.414, color: "#f43f5e" },
  { id: "2.272", enabled: false, ratio: 2.272, color: "#f59e0b" },
  { id: "2.414", enabled: false, ratio: 2.414, color: "#10b981" },
];

export default function FibonacciSettingsModal({
  isOpen,
  onClose,
  fibDrawing,
  onSaveFibDrawing,
}) {
  const [activeTab, setActiveTab] = useState("style");

  // Local editable state
  const [levels, setLevels] = useState(DEFAULT_FIB_LEVELS);
  const [showTrendline, setShowTrendline] = useState(true);
  const [trendlineColor, setTrendlineColor] = useState("#94a3b8");
  const [showBackground, setShowBackground] = useState(true);
  const [backgroundOpacity, setBackgroundOpacity] = useState(0.12);
  const [extendRight, setExtendRight] = useState(false);
  const [showPrices, setShowPrices] = useState(true);
  const [showLevels, setShowLevels] = useState(true);

  // Price coordinates
  const [price1, setPrice1] = useState(0);
  const [price2, setPrice2] = useState(0);

  useEffect(() => {
    if (fibDrawing) {
      const savedTpl = loadSavedFibTemplate();
      const defaultLevels = (savedTpl && savedTpl.fibLevels) ? savedTpl.fibLevels : DEFAULT_FIB_LEVELS;
      setLevels(fibDrawing.fibLevels || defaultLevels);
      setShowTrendline(fibDrawing.showTrendline !== undefined ? fibDrawing.showTrendline : (savedTpl?.showTrendline !== undefined ? savedTpl.showTrendline : true));
      setTrendlineColor(fibDrawing.trendlineColor || savedTpl?.trendlineColor || "#94a3b8");
      setShowBackground(fibDrawing.showBackground !== undefined ? fibDrawing.showBackground : (savedTpl?.showBackground !== undefined ? savedTpl.showBackground : true));
      setBackgroundOpacity(fibDrawing.backgroundOpacity !== undefined ? fibDrawing.backgroundOpacity : (savedTpl?.backgroundOpacity !== undefined ? savedTpl.backgroundOpacity : 0.12));
      setExtendRight(fibDrawing.extendRight !== undefined ? fibDrawing.extendRight : (savedTpl?.extendRight !== undefined ? savedTpl.extendRight : false));
      setShowPrices(fibDrawing.showPrices !== undefined ? fibDrawing.showPrices : (savedTpl?.showPrices !== undefined ? savedTpl.showPrices : true));
      setShowLevels(fibDrawing.showLevels !== undefined ? fibDrawing.showLevels : (savedTpl?.showLevels !== undefined ? savedTpl.showLevels : true));
      setPrice1(fibDrawing.p1?.price || 0);
      setPrice2(fibDrawing.p2?.price || 0);
    }
  }, [fibDrawing, isOpen]);

  if (!isOpen || !fibDrawing) return null;

  const handleLevelToggle = (index) => {
    const next = [...levels];
    next[index].enabled = !next[index].enabled;
    setLevels(next);
  };

  const handleLevelRatioChange = (index, val) => {
    const next = [...levels];
    const num = parseFloat(val);
    next[index].ratio = isNaN(num) ? val : num;
    setLevels(next);
  };

  const handleLevelColorChange = (index, color) => {
    const next = [...levels];
    next[index].color = color;
    setLevels(next);
  };

  const handleReset = () => {
    setLevels(DEFAULT_FIB_LEVELS);
    setShowTrendline(true);
    setTrendlineColor("#94a3b8");
    setShowBackground(true);
    setBackgroundOpacity(0.12);
    setExtendRight(false);
    setShowPrices(true);
    setShowLevels(true);
  };

  const handleSave = () => {
    const templateData = {
      fibLevels: levels,
      showTrendline,
      trendlineColor,
      showBackground,
      backgroundOpacity,
      extendRight,
      showPrices,
      showLevels,
    };
    saveFibTemplate(templateData);

    const updated = {
      ...fibDrawing,
      ...templateData,
      p1: { ...fibDrawing.p1, price: parseFloat(price1) || fibDrawing.p1.price },
      p2: { ...fibDrawing.p2, price: parseFloat(price2) || fibDrawing.p2.price },
    };
    onSaveFibDrawing(updated);
    onClose();
  };

  // Split levels into 2 columns like TradingView
  const col1 = levels.slice(0, 8);
  const col2 = levels.slice(8);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4 animate-fadeIn">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl flex flex-col font-sans max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-800 bg-slate-950/50">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-cyan-500/20 text-cyan-400">
              <Sliders className="w-4 h-4" />
            </div>
            <h3 className="text-sm font-bold text-slate-100">Fib thoái lui</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-800 bg-slate-950/30 px-6 gap-6 text-xs font-semibold">
          <button
            onClick={() => setActiveTab("style")}
            className={`py-2.5 transition border-b-2 cursor-pointer ${
              activeTab === "style"
                ? "border-cyan-400 text-cyan-400 font-bold"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            Định dạng
          </button>
          <button
            onClick={() => setActiveTab("coords")}
            className={`py-2.5 transition border-b-2 cursor-pointer ${
              activeTab === "coords"
                ? "border-cyan-400 text-cyan-400 font-bold"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            Tọa độ
          </button>
          <button
            onClick={() => setActiveTab("visibility")}
            className={`py-2.5 transition border-b-2 cursor-pointer ${
              activeTab === "visibility"
                ? "border-cyan-400 text-cyan-400 font-bold"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            Hiển thị
          </button>
        </div>

        {/* Body Content */}
        <div className="p-6 overflow-y-auto space-y-5 text-xs text-slate-300">
          {activeTab === "style" && (
            <div className="space-y-4">
              {/* General Controls */}
              <div className="grid grid-cols-2 gap-3 pb-3 border-b border-slate-800">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showTrendline}
                    onChange={(e) => setShowTrendline(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-950 text-cyan-500 focus:ring-0 cursor-pointer"
                  />
                  <span>Đường Xu hướng</span>
                  <input
                    type="color"
                    value={trendlineColor}
                    onChange={(e) => setTrendlineColor(e.target.value)}
                    className="w-5 h-5 rounded border-0 bg-transparent cursor-pointer ml-auto"
                  />
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={extendRight}
                    onChange={(e) => setExtendRight(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-950 text-cyan-500 focus:ring-0 cursor-pointer"
                  />
                  <span>Kéo dài sang phải</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showBackground}
                    onChange={(e) => setShowBackground(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-950 text-cyan-500 focus:ring-0 cursor-pointer"
                  />
                  <span>Đổ màu nền dải</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showPrices}
                    onChange={(e) => setShowPrices(e.target.checked)}
                    className="rounded border-slate-700 bg-slate-950 text-cyan-500 focus:ring-0 cursor-pointer"
                  />
                  <span>Hiện nhãn mức giá</span>
                </label>
              </div>

              {/* Levels Grid (2 Columns TradingView Style) */}
              <div className="space-y-2">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">
                  CÁC MỨC TỶ LỆ FIBONACCI (LEVELS & COLORS)
                </span>

                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5">
                  {/* Column 1 */}
                  <div className="space-y-2">
                    {col1.map((lvl, idx) => (
                      <div key={lvl.id || idx} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={lvl.enabled}
                          onChange={() => handleLevelToggle(idx)}
                          className="rounded border-slate-700 bg-slate-950 text-cyan-500 focus:ring-0 cursor-pointer"
                        />
                        <input
                          type="text"
                          value={lvl.ratio}
                          onChange={(e) => handleLevelRatioChange(idx, e.target.value)}
                          className="w-16 bg-slate-950 border border-slate-800 rounded px-2 py-0.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-cyan-500"
                        />
                        <input
                          type="color"
                          value={lvl.color}
                          onChange={(e) => handleLevelColorChange(idx, e.target.value)}
                          className="w-6 h-6 rounded border-0 bg-transparent cursor-pointer ml-auto"
                        />
                      </div>
                    ))}
                  </div>

                  {/* Column 2 */}
                  <div className="space-y-2">
                    {col2.map((lvl, idx) => {
                      const realIdx = idx + col1.length;
                      return (
                        <div key={lvl.id || realIdx} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={lvl.enabled}
                            onChange={() => handleLevelToggle(realIdx)}
                            className="rounded border-slate-700 bg-slate-950 text-cyan-500 focus:ring-0 cursor-pointer"
                          />
                          <input
                            type="text"
                            value={lvl.ratio}
                            onChange={(e) => handleLevelRatioChange(realIdx, e.target.value)}
                            className="w-16 bg-slate-950 border border-slate-800 rounded px-2 py-0.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-cyan-500"
                          />
                          <input
                            type="color"
                            value={lvl.color}
                            onChange={(e) => handleLevelColorChange(realIdx, e.target.value)}
                            className="w-6 h-6 rounded border-0 bg-transparent cursor-pointer ml-auto"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "coords" && (
            <div className="space-y-4">
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">
                TỌA ĐỘ MỨC GIÁ ANCHOR
              </span>
              <div className="space-y-3">
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1">Mức giá Điểm 1 (Gốc sóng / Start):</label>
                  <input
                    type="number"
                    step="any"
                    value={price1}
                    onChange={(e) => setPrice1(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs font-mono text-slate-200 focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1">Mức giá Điểm 2 (Ngọn sóng / End):</label>
                  <input
                    type="number"
                    step="any"
                    value={price2}
                    onChange={(e) => setPrice2(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-1.5 text-xs font-mono text-slate-200 focus:outline-none focus:border-cyan-500"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "visibility" && (
            <div className="space-y-3 text-xs text-slate-300">
              <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider block">
                HIỂN THỊ TRÊN KHUNG THỜI GIAN
              </span>
              <p className="text-slate-400">Hiển thị đầy đủ trên mọi khung thời gian (M1, M5, M15, M30, H1, H4, D1).</p>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="px-6 py-3.5 border-t border-slate-800 flex items-center justify-between bg-slate-950/60 text-xs">
          <button
            onClick={handleReset}
            className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 transition cursor-pointer"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Mặc định</span>
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold transition cursor-pointer"
            >
              Hủy bỏ
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-1.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold transition cursor-pointer shadow-md shadow-cyan-500/20"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
